/* ─── GLSL ─── */

const VERT300 = `#version 300 es
layout(location = 0) in vec2 pos;
void main() { gl_Position = vec4(pos, 0.0, 1.0); }
`

function commonUtils(userCode: string): string {
  // 用户未自定义 pal 时注入 iq 的 cosine 调色板
  const pal = !/\bvec3\s+pal\s*\(/.test(userCode)
  return `
#define iGlobalTime iTime
#define texture2D _st
#define textureCube _st
${pal ? `vec3 pal(in float t, in vec3 a, in vec3 b, in vec3 c, in vec3 d) { return a + b*cos(6.28318*(c*t+d)); }` : ''}

// texture() 直接用 GLSL ES 3.00 内建(原生支持 2/3 参数);
// texture2D/textureCube 为 WebGL1 老式调用, 映射到 _st(显式 textureLod, 兼容 vec3-uv 写法)。
vec4 _st(sampler2D s, vec2 uv)            { return textureLod(s, uv, 0.0); }
vec4 _st(sampler2D s, vec3 uvw)           { return textureLod(s, uvw.xy, 0.0); }
vec4 _st(sampler2D s, vec2 uv, float b)   { return textureLod(s, uv, b); }
vec4 _st(sampler2D s, vec3 uvw, float b)  { return textureLod(s, uvw.xy, b); }
vec4 _st(samplerCube s, vec3 uvw)         { return textureLod(s, uvw, 0.0); }
vec4 _st(samplerCube s, vec2 uv)          { return textureLod(s, vec3(uv, 0.0), 0.0); }
vec4 _st(samplerCube s, vec3 uvw, float b){ return textureLod(s, uvw, b); }
vec4 _st(samplerCube s, vec2 uv, float b) { return textureLod(s, vec3(uv, 0.0), b); }
`
}

/** 组装单个 pass 的完整片元源码 */
export function passSrc(common: string, code: string, chTypes: string[]): string {
  let c = (common + '\n' + code)
    .replace(/precision\s+\w+\s+float\s*;\s*\n?/gi, '')
    .replace(/#version\s+\S+\s*\n?/g, '')

  // 用户自定义 texture 函数(返回 vec/float/int)与内建/宏冲突, 仅替换函数形式 texture(
  if (/\b(vec[234]|float|int)\s+texture\s*\(/.test(c)) {
    c = c.replace(/\btexture\s*\(/g, 'texFetch(')
  }

  return `#version 300 es
#define HW_PERFORMANCE 1
precision highp float;
precision highp int;
precision mediump sampler3D;

uniform vec3 iResolution;
uniform float iTime;
uniform float iTimeDelta;
uniform float iFrameRate;
uniform int iFrame;
uniform vec4 iMouse;
uniform vec4 iDate;
uniform float iSampleRate;
uniform float iChannelTime[4];
uniform vec3 iChannelResolution[4];
uniform int iCubeFace;
${chTypes.map((t, i) => `uniform ${t} iChannel${i};`).join('\n')}

${commonUtils(c)}

out vec4 shadertoy_out_color;

void st_assert(bool cond, int v) { if(!cond){ if(v==0) shadertoy_out_color.x=-1.0; else if(v==1) shadertoy_out_color.y=-1.0; else if(v==2) shadertoy_out_color.z=-1.0; else shadertoy_out_color.w=-1.0; } }
void st_assert(bool cond) { if(!cond) shadertoy_out_color.x=-1.0; }

void mainImage(out vec4 fragColor, in vec2 fragCoord);

void main() {
    shadertoy_out_color = vec4(1.0, 1.0, 1.0, 1.0);
    vec4 color = vec4(1e20); // 未赋值时 1e20 被钳制为白色
    mainImage(color, gl_FragCoord.xy);
    // st_assert 调试: 断言失败把 shadertoy_out_color 对应通道置负, 触发红/绿/蓝/黄屏
    if (shadertoy_out_color.x < 0.0) color = vec4(1.0, 0.0, 0.0, 1.0);
    if (shadertoy_out_color.y < 0.0) color = vec4(0.0, 1.0, 0.0, 1.0);
    if (shadertoy_out_color.z < 0.0) color = vec4(0.0, 0.0, 1.0, 1.0);
    if (shadertoy_out_color.w < 0.0) color = vec4(1.0, 1.0, 0.0, 1.0);
    shadertoy_out_color = vec4(color.xyz, 1.0);
}

${c}
`
}

function makeShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!
  gl.shaderSource(s, src)
  gl.compileShader(s)
  return s
}

/** 编译 + 链接完整片元着色器; 不抛异常, 失败返回错误详情(供调用方决定抛错或保留旧版) */
export function buildProgram(
  gl: WebGL2RenderingContext,
  fullSrc: string
): { prog: WebGLProgram | null; err: string } {
  const vs = makeShader(gl, gl.VERTEX_SHADER, VERT300)
  if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
    const err = '顶点着色器编译失败:\n' + (gl.getShaderInfoLog(vs) || '(无详情)')
    gl.deleteShader(vs)
    return { prog: null, err }
  }
  const fs = makeShader(gl, gl.FRAGMENT_SHADER, fullSrc)
  if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
    const err = '着色器编译失败:\n' + (gl.getShaderInfoLog(fs) || '(无详情)')
    gl.deleteShader(vs)
    gl.deleteShader(fs)
    return { prog: null, err }
  }
  const prog = gl.createProgram()!
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const err = '着色器链接失败:\n' + (gl.getProgramInfoLog(prog) || '(无详情)')
    gl.deleteProgram(prog)
    return { prog: null, err }
  }
  return { prog, err: '' }
}